import React, { useContext, useEffect, useMemo, useState } from 'react';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core/';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ProgressBar from '@ramonak/react-progress-bar';

import Dropdown from 'components/Dropdown';
import Loading from 'components/animations/Loading';
import useSenslaApi from 'hooks/useSenslaApi';
import { ctx } from 'store';
import { colors } from 'styles';

import { useStyles } from './styles';

const ALL_CHANNELS = 'All-channels';
const ALL_GROUPS = 'All-teams';

const SurveysReport: React.FC = () => {
  const {
    teams: { currentTeam },
  } = useContext(ctx.store);
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [daysSelected, setDaysSelected] = useState(14);
  const [channels, setChannels] = useState<{ id: string }[]>([]);
  const [groups, setGroups] = useState<{ id: string }[]>([]);
  const [expanded, setExpanded] = useState<number>(999);
  const queryParams: any = queryString.parse(location.search);
  const { channelId, groupId } = queryParams;
  const [channel, setChannel] = useState(channelId ? channelId : ALL_CHANNELS);
  const [group, setGroup] = useState(groupId ? groupId : ALL_GROUPS);
  const query = queryString.stringify(
    {
      days: daysSelected,
      // channel: channel === ALL_CHANNELS ? null : channel,
      // group: group === ALL_GROUPS ? null : group,
      team: currentTeam.teamId ? currentTeam.teamId : currentTeam.tenantId,
      platform: currentTeam.teamId ? 'slack' : 'microsoft',
    },
    {
      skipNull: true,
    }
  );

  const [{ data: surveysReport, loading }] = useSenslaApi(`surveys/report?${query}`, 'GET', true);

  useEffect(() => {
    const data = [
      ...(currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels)
        .map((ch: any) => {
          if (ch.monitored) {
            return { id: ch.channelId };
          }
        })
        .filter((c: any) => c),
    ];
    const groupsData = [
      ...(currentTeam.teamId ? [] : currentTeam.msGroups)
        .filter((gr: any) => gr.monitored)
        .map((group: any) => {
          return { id: group.groupId };
        }),
    ];
    onChange(channelId, 'channels', true);
    setSelectedChannel(channelId);
    setChannels(data);
    if (!currentTeam.teamId) {
      onChange(groupId, 'teams', true);
      setSelectedGroup(groupId);
      setGroups(groupsData);
    }
    setDaysSelected(14);
  }, []);

  // Counters
  const onChange = (value: string, type: string, fromEffect?: boolean) => {
    if (!fromEffect) {
      let groupQuery =
        group || type === 'teams' ? `groupId=${type === 'teams' ? value : group}` : '';
      let channelQuery =
        channel || type === 'channels' ? `channelId=${type === 'channels' ? value : channel}` : '';

      groupQuery = groupQuery === 'groupId=All-teams' ? '' : groupQuery;
      channelQuery = channelQuery === 'channelId=All-channels' ? '' : channelQuery;

      let completeQuery;
      if (groupQuery !== '' && channelQuery !== '') {
        completeQuery = `?${groupQuery}&${channelQuery}`;
      } else if (groupQuery === '') {
        completeQuery = `?${channelQuery}`;
      } else if (channelQuery === '') {
        completeQuery = `?${groupQuery}`;
      }

      history.push(`/dashboard/surveys-report${completeQuery}`);
    }

    if (type === 'channels') {
      setSelectedChannel(value);
      const filteredChannel = (currentTeam.teamId
        ? currentTeam.channels
        : currentTeam.msChannels
      ).filter((channel: any) => channel.channelId === value)[0];
      if (filteredChannel) {
        setChannel(filteredChannel.channelId);
      } else {
        setChannel('');
      }
    } else if (type === 'teams') {
      setSelectedGroup(value);
      const filteredGroup = currentTeam.msGroups.filter((group: any) => group.groupId === value)[0];
      if (filteredGroup) {
        setGroup(filteredGroup.groupId);
      } else {
        setGroup('');
      }
    }
  };

  const dropdownChannels = useMemo(() => {
    if (!groupId || groupId === '') {
      return channels;
    }
    return currentTeam.msGroups
      .find((group: any) => group.groupId === groupId)
      .channels.map((channel: any) => ({ id: channel }));
  }, [groupId, channels]);

  const onChangeDate = (value: any) => {
    setDaysSelected(value);
  };

  const handleChange = (panel: number) => () => {
    setExpanded(panel === expanded ? 999 : panel);
  };

  return (
    <div style={{ height: '100vh' }}>
      <Box display="flex" p={1} alignItems="center" className={classes.header}>
        <Box p={1} flexGrow={1}>
          <Typography component="h1" variant="h5">
            Surveys Report
          </Typography>
        </Box>
        <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            data={[{ id: 14 }, { id: 30 }, { id: 90 }]}
            translate={'days'}
            type="days"
            setValue={onChangeDate}
            value={String(daysSelected)}
            defaultValue={String(daysSelected)}
          />
        </Box>
        {!currentTeam.teamId ? (
          <Box p={1} flexShrink={0} className={classes.channelSelect}>
            <Dropdown
              addWorkspace
              data={groups}
              translate={currentTeam.msGroups}
              type="teams"
              setValue={onChange}
              value={selectedGroup}
              autocomplete
              defaultValue={groupId}
            />
          </Box>
        ) : null}
        {/* <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            addWorkspace
            data={dropdownChannels}
            translate={currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels}
            type="channels"
            setValue={onChange}
            value={selectedChannel}
            autocomplete
            defaultValue={channelId}
          />
        </Box> */}
      </Box>
      {!loading ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper className={classes.papersColumn}>
              <Grid
                style={{
                  alignItems: 'center',
                  borderBottom: `2px solid ${colors.greyBg}`,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: '10px',
                  padding: '5px 0',
                }}
              >
                <Typography component="h2" className={classes.subtitle}>
                  {`SURVEYS RESULTS`}
                </Typography>
              </Grid>
              <Grid className={classes.gridRow}>
                <Grid xs={4} className={classes.titleGrid}>
                  <Typography
                    component="h2"
                    className={classes.subtitle}
                    style={{ marginBottom: '20px' }}
                  >
                    {`SURVEYS INVOLVED`}
                  </Typography>

                  {surveysReport &&
                    surveysReport.surveys.map((survey: any, index: number) => (
                      <Accordion
                        key={index}
                        expanded={expanded === index}
                        onChange={handleChange(index)}
                        className={classes.accordion}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1bh-content"
                          id="panel1bh-header"
                        >
                          <Typography
                            className={classes.heading}
                          >{`# of Answers: ${survey.answersCount}`}</Typography>
                          <Typography className={classes.secondaryHeading}>
                            {`${survey.startDate} - ${survey.endDate}`}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid className={classes.questions}>
                            {survey.questions.map((question: any, index: number) => (
                              <Typography className={classes.questionsText} key={index}>
                                {`* ${question}`}
                              </Typography>
                            ))}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                </Grid>
                <Grid xs={8} style={{ padding: '0 20px' }}>
                  {surveysReport &&
                    Object.entries(surveysReport.skillsAverage).map((item: any, index: number) => (
                      <Grid key={index} style={{ margin: '25px 10px', padding: '5px 10px' }}>
                        <Grid className={classes.gridRow}>
                          <Typography
                            component="h2"
                            className={classes.subtitle}
                            style={{ textAlign: 'center' }}
                          >
                            {item[0]}
                          </Typography>
                          <Typography
                            component="h2"
                            className={classes.subtitle}
                            style={{ textAlign: 'center' }}
                          >
                            {item[1].average.toFixed(1)}
                          </Typography>
                        </Grid>
                        <ProgressBar
                          completed={item[1].average * 10}
                          isLabelVisible={false}
                          bgColor={colors.blueBrand}
                          transitionDuration="3s"
                        />
                      </Grid>
                    ))}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <div className={classes.loading}>
          <Loading containerHeight="70vh" />
        </div>
      )}
    </div>
  );
};

export { SurveysReport };
